import { useEffect } from "react";
import type { PlanEvent } from "../../../shared/types";
import type { CalendarNavigationState } from "./use-calendar-navigation";
import { setClipboardEvent, getClipboardEvent } from "../stores/clipboard-store";
import { getHoverPosition } from "../stores/hover-position-store";

interface KeyboardNavigationOptions {
  readonly nav: CalendarNavigationState;
  readonly setShowAddForm: (show: boolean) => void;
  readonly setShowFullEditForm: (show: boolean) => void;
  readonly setEditingEvent: (event: null) => void;
  readonly hasEditingEvent: boolean;
  readonly editingEvent: PlanEvent | null;
  readonly onDeleteEditingEvent: (() => void) | undefined;
  readonly onPasteEvent: ((date: string, startTime?: string) => void) | undefined;
}

export function useKeyboardNavigation({
  nav,
  setShowAddForm,
  setShowFullEditForm,
  setEditingEvent,
  hasEditingEvent,
  editingEvent,
  onDeleteEditingEvent,
  onPasteEvent,
}: KeyboardNavigationOptions) {
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      // Ctrl/Cmd+C: copy selected event
      if ((e.ctrlKey || e.metaKey) && e.key === "c" && editingEvent) {
        e.preventDefault();
        setClipboardEvent(editingEvent);
        return;
      }

      // Ctrl/Cmd+V: paste event at mouse position or selected date
      if ((e.ctrlKey || e.metaKey) && e.key === "v" && getClipboardEvent()) {
        e.preventDefault();
        const hover = getHoverPosition();
        const targetDate = hover?.date ?? nav.selectedDate ?? nav.currentDate;
        onPasteEvent?.(targetDate, hover?.time);
        return;
      }

      if (e.ctrlKey || e.metaKey || e.altKey) return;
      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          nav.goToPrev();
          break;
        case "ArrowRight":
          e.preventDefault();
          nav.goToNext();
          break;
        case "t":
          nav.goToToday();
          break;
        case "d":
          nav.setCalendarMode("day");
          break;
        case "w":
          nav.setCalendarMode("week");
          break;
        case "m":
          nav.setCalendarMode("month");
          break;
        case "n":
          setShowAddForm(true);
          setEditingEvent(null);
          break;
        case "Delete":
        case "Backspace":
          if (hasEditingEvent && onDeleteEditingEvent) {
            e.preventDefault();
            onDeleteEditingEvent();
          }
          break;
        case "Escape":
          setShowAddForm(false);
          setShowFullEditForm(false);
          setEditingEvent(null);
          break;
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [
    nav,
    setShowAddForm,
    setShowFullEditForm,
    setEditingEvent,
    hasEditingEvent,
    editingEvent,
    onDeleteEditingEvent,
    onPasteEvent,
  ]);
}
